import { useEffect, useRef, useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/d6up6fm7ipaugi04lcq0";

const insuranceTypes = [
  "Individual Health Plan",
  "Family Health Plan",
  "Medicare Advantage",
  "Medicare Supplement (Medigap)",
  "Part D Prescription Plan",
  "Dental & Vision",
  "Small Business / Group Plan",
  "Supplemental Coverage",
  "Not Sure – Need Guidance",
];

export default function ContactSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const messageField = form.querySelector<HTMLTextAreaElement>("textarea[name='message']");
    if (messageField && messageField.value.length > 500) {
      alert("Message must be 500 characters or less.");
      return;
    }
    setStatus("sending");
    try {
      const data = new FormData(form);
      const body = new URLSearchParams();
      data.forEach((val, key) => body.append(key, val as string));
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="py-24 bg-emerald-900" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/15 text-amber-300 text-xs font-['Open_Sans'] font-600 tracking-widest uppercase mb-4 border border-white/20">
            Get In Touch
          </span>
          <h2 className="font-['Poppins'] font-700 text-4xl lg:text-5xl text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="font-['Open_Sans'] text-white/75 max-w-xl mx-auto text-base leading-relaxed">
            Our licensed <strong className="text-white">health insurance</strong> agents in Irving, TX are available 24/7 to help you find the right coverage.
          </p>
        </div>

        {/* Contact Info Row */}
        <div className={`grid md:grid-cols-3 gap-6 mb-12 transition-all duration-700 delay-150 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <a href="tel:9722174662" className="flex items-center gap-4 bg-white/10 border border-white/20 rounded-lg p-5 hover:bg-white/15 transition-colors cursor-pointer group">
            <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <i className="ri-phone-fill text-amber-400 text-xl"></i>
            </div>
            <div>
              <div className="font-['Open_Sans'] text-white/60 text-xs mb-0.5 uppercase tracking-wide">Call Us Anytime</div>
              <div className="font-['Poppins'] font-700 text-white text-lg group-hover:text-amber-400 transition-colors">972-217-4662</div>
            </div>
          </a>
          <div className="flex items-center gap-4 bg-white/10 border border-white/20 rounded-lg p-5">
            <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <i className="ri-map-pin-2-fill text-amber-400 text-xl"></i>
            </div>
            <div>
              <div className="font-['Open_Sans'] text-white/60 text-xs mb-0.5 uppercase tracking-wide">Our Office</div>
              <div className="font-['Poppins'] font-600 text-white text-sm leading-snug">1800 Market Pl Blvd #140<br />Irving, TX 75063</div>
            </div>
          </div>
          <div className="flex items-center gap-4 bg-white/10 border border-white/20 rounded-lg p-5">
            <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <i className="ri-time-fill text-amber-400 text-xl"></i>
            </div>
            <div>
              <div className="font-['Open_Sans'] text-white/60 text-xs mb-0.5 uppercase tracking-wide">Business Hours</div>
              <div className="font-['Poppins'] font-700 text-amber-400 text-lg">Open 24/7</div>
            </div>
          </div>
        </div>

        {/* Form + Map */}
        <div className={`grid lg:grid-cols-5 gap-8 transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Form */}
          <div className="lg:col-span-3 bg-white rounded-lg p-8">
            <h3 className="font-['Poppins'] font-700 text-xl text-emerald-900 mb-2">Send Us a Message</h3>
            <p className="font-['Open_Sans'] text-gray-500 text-sm mb-6">A licensed agent will respond within minutes, day or night.</p>

            {status === "success" ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-4xl"></i>
                </div>
                <h4 className="font-['Poppins'] font-700 text-lg text-emerald-900 mb-2">Message Sent!</h4>
                <p className="font-['Open_Sans'] text-gray-500 text-sm max-w-xs">
                  Thank you for reaching out. One of our licensed agents will contact you shortly. Remember, you can also call us anytime at{" "}
                  <a href="tel:9722174662" className="text-emerald-700 font-600 cursor-pointer">972-217-4662</a>.
                </p>
                <button onClick={() => setStatus("idle")} className="mt-5 px-5 py-2 rounded-lg border-2 border-emerald-700 text-emerald-700 text-sm font-['Poppins'] font-600 cursor-pointer hover:bg-emerald-700 hover:text-white transition-colors whitespace-nowrap">
                  Send Another Message
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} id="contact-form" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-['Open_Sans'] font-600 text-gray-700 mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="John Smith"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Open_Sans'] text-gray-800 focus:outline-none focus:border-emerald-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-['Open_Sans'] font-600 text-gray-700 mb-1.5">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@email.com"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Open_Sans'] text-gray-800 focus:outline-none focus:border-emerald-500 transition-colors"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-['Open_Sans'] font-600 text-gray-700 mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="(972) 000-0000"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Open_Sans'] text-gray-800 focus:outline-none focus:border-emerald-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-['Open_Sans'] font-600 text-gray-700 mb-1.5">Number of People to Cover</label>
                    <select
                      name="covered_count"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Open_Sans'] text-gray-800 focus:outline-none focus:border-emerald-500 transition-colors cursor-pointer"
                    >
                      <option value="">Select...</option>
                      <option value="1">Just Me</option>
                      <option value="2">Me + Spouse</option>
                      <option value="3-4">Family (3-4)</option>
                      <option value="5+">Family (5+)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-['Open_Sans'] font-600 text-gray-700 mb-1.5">Type of Insurance Needed</label>
                  <select
                    name="insurance_type"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Open_Sans'] text-gray-800 focus:outline-none focus:border-emerald-500 transition-colors cursor-pointer"
                  >
                    <option value="">Select plan type...</option>
                    {insuranceTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-['Open_Sans'] font-600 text-gray-700 mb-1.5">
                    Message <span className="text-gray-400 font-400">({charCount}/500 characters)</span>
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us about your health insurance needs, current situation, or any questions..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm font-['Open_Sans'] text-gray-800 focus:outline-none focus:border-emerald-500 transition-colors resize-none"
                  ></textarea>
                </div>

                {status === "error" && (
                  <div className="flex items-center gap-2 text-red-600 text-sm font-['Open_Sans'] bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                    <i className="ri-error-warning-line"></i>
                    Something went wrong. Please try again or call us at 972-217-4662.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="w-full py-4 bg-amber-500 hover:bg-amber-400 disabled:bg-gray-300 text-white font-['Poppins'] font-700 text-sm rounded-lg flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {status === "sending" ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                  ) : (
                    <><i className="ri-send-plane-fill"></i> Send Message — It's Free</>
                  )}
                </button>
                <p className="text-center text-xs font-['Open_Sans'] text-gray-400">
                  No spam. No fees. A licensed agent will respond within minutes.
                </p>
              </form>
            )}
          </div>

          {/* Map */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="rounded-lg overflow-hidden border border-white/20 flex-1 min-h-72">
              <iframe
                title="HealthMarkets Insurance Agency office location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3351.3!2d-96.9464!3d32.8784!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864e83b0a4f49b4d%3A0x5b2e1aee16e4b9e0!2s1800%20Market%20Pl%20Blvd%20%23140%2C%20Irving%2C%20TX%2075063!5e0!3m2!1sen!2sus!4v1711000000001"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "288px" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
            <a
              href="https://maps.app.goo.gl/grzX3dievhN3giBv8"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center justify-center gap-2 py-3.5 rounded-lg border-2 border-white/40 text-white font-['Poppins'] font-600 text-sm hover:border-amber-400 hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-google-fill"></i>
              View on Google Maps
            </a>
            <div className="bg-white/10 border border-white/20 rounded-lg p-5">
              <div className="font-['Poppins'] font-600 text-white text-sm mb-3">Prefer to call directly?</div>
              <a href="tel:9722174662" className="flex items-center gap-3 text-amber-400 font-['Poppins'] font-700 text-2xl cursor-pointer hover:text-amber-300 transition-colors">
                <i className="ri-phone-fill text-2xl"></i>
                972-217-4662
              </a>
              <div className="font-['Open_Sans'] text-white/60 text-xs mt-1">Available 24 hours, 7 days a week</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
